import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp, Search } from 'lucide-react';
import { formatCurrency } from '@/lib/format';
import i18n from '@/lib/i18n';
import {
  usePlatformTenants,
  type PlatformTenantRow,
  type PlatformTenantsParams,
} from '@/api/hooks/usePlatformTenants';

const PAGE_SIZE = 20;

const STATUS_OPTIONS = ['ALL', 'ACTIVE', 'PENDING', 'SUSPENDED'] as const;

type SortableField = NonNullable<PlatformTenantsParams['sortBy']>;

const COLUMNS: Array<{
  key: string;
  label: string;
  sortBy?: SortableField;
  className?: string;
  align?: 'left' | 'right';
}> = [
  { key: 'operator', label: 'Operator', sortBy: 'name' },
  { key: 'status', label: 'Status', sortBy: 'status' },
  { key: 'plan', label: 'Plan', sortBy: 'planTier' },
  { key: 'bookings', label: 'Bookings', sortBy: 'totalBookings', align: 'right' },
  { key: 'revenue', label: 'Revenue', sortBy: 'totalRevenue', align: 'right' },
  { key: 'fees', label: 'Platform Fee', sortBy: 'platformFeeCollected', align: 'right' },
  { key: 'stripe', label: 'Stripe', sortBy: 'stripeConnectStatus' },
  { key: 'fee-override', label: 'Fee %' },
  { key: 'signed-up', label: 'Signed Up', sortBy: 'createdAt' },
  { key: 'last-active', label: 'Last Active', sortBy: 'lastActivityAt' },
];

/**
 * Platform admin tenants table. Single primary view for Karim — every
 * operator at a glance with the fields he needs to triage health
 * (Stripe Connect state, recent activity, revenue). Sorting + search +
 * status filter are server-driven so the view stays coherent when
 * more tenants land.
 *
 * <p>Clicking a row currently fires a toast — {@code /platform/tenants/:id}
 * ships in Evening 2.
 */
export default function PlatformTenantsPage() {
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState<SortableField>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, statusFilter, sortBy, sortDir]);

  const params = useMemo<PlatformTenantsParams>(
    () => ({
      page,
      size: PAGE_SIZE,
      sortBy,
      sortDir,
      status: statusFilter !== 'ALL' ? statusFilter : undefined,
      search: debouncedSearch || undefined,
    }),
    [page, sortBy, sortDir, statusFilter, debouncedSearch],
  );

  const { data, isLoading, isError } = usePlatformTenants(params);

  const tenants = data?.tenants ?? [];
  const pagination = data?.pagination;

  const toggleSort = (field: SortableField | undefined) => {
    if (!field) return;
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  const handleRowClick = (t: PlatformTenantRow) => {
    navigate(`/platform/tenants/${t.id}`);
  };

  return (
    <div className="px-6 py-8 md:px-10 md:py-10">
      <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-400">
            Platform Admin
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-neutral-900 md:text-3xl">
            Tenants
          </h1>
          <p className="mt-1 text-sm text-neutral-500">
            Every operator on the RekPlay platform at a glance.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/platform/onboarding')}
          className="inline-flex items-center gap-2 rounded-md bg-[#C2713A] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#a95f2f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A] focus-visible:ring-offset-2"
        >
          + Onboard operator
        </button>
      </header>

      <section className="mt-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
              aria-hidden="true"
            />
            <input
              type="search"
              placeholder="Search operators…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-64 rounded-md border border-neutral-200 bg-white py-2 pl-8 pr-3 text-sm text-neutral-700 placeholder:text-neutral-400 focus-visible:border-[#C2713A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A]/30"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-700 focus-visible:border-[#C2713A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A]/30"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s === 'ALL' ? 'All statuses' : s.charAt(0) + s.slice(1).toLowerCase()}
              </option>
            ))}
          </select>
        </div>
        <p className="text-sm text-neutral-500">
          {pagination
            ? `${pagination.totalElements} ${pagination.totalElements === 1 ? 'operator' : 'operators'}`
            : '—'}
        </p>
      </section>

      <div className="mt-4 overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs font-semibold uppercase tracking-wider text-neutral-500">
              <tr>
                {COLUMNS.map((col) => {
                  const active = col.sortBy === sortBy;
                  return (
                    <th
                      key={col.key}
                      scope="col"
                      className={
                        'whitespace-nowrap px-4 py-3 ' +
                        (col.align === 'right' ? 'text-right' : 'text-left')
                      }
                    >
                      {col.sortBy ? (
                        <button
                          type="button"
                          onClick={() => toggleSort(col.sortBy)}
                          className={
                            'inline-flex items-center gap-1 transition-colors ' +
                            (active ? 'text-[#C2713A]' : 'hover:text-neutral-700')
                          }
                        >
                          {col.label}
                          {active ? (
                            sortDir === 'asc' ? (
                              <ArrowUp className="h-3 w-3" aria-hidden="true" />
                            ) : (
                              <ArrowDown className="h-3 w-3" aria-hidden="true" />
                            )
                          ) : null}
                        </button>
                      ) : (
                        col.label
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 bg-white">
              {isLoading && (
                <tr>
                  <td colSpan={COLUMNS.length} className="px-4 py-10 text-center text-neutral-400">
                    Loading tenants…
                  </td>
                </tr>
              )}
              {isError && !isLoading && (
                <tr>
                  <td colSpan={COLUMNS.length} className="px-4 py-10 text-center text-red-600">
                    Failed to load tenants. Try reloading the page.
                  </td>
                </tr>
              )}
              {!isLoading && !isError && tenants.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} className="px-4 py-10 text-center text-neutral-400">
                    No operators match the current filters.
                  </td>
                </tr>
              )}
              {tenants.map((t) => (
                <tr
                  key={t.id}
                  onClick={() => handleRowClick(t)}
                  className="cursor-pointer transition-colors hover:bg-neutral-50"
                >
                  <td className="whitespace-nowrap px-4 py-3">
                    <p className="font-medium text-neutral-900">{t.name}</p>
                    <p className="text-xs text-neutral-400 font-mono">{t.slug}</p>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <StatusBadge status={t.status} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-neutral-700">
                    {t.planTier ?? '—'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-neutral-900">
                    {t.totalBookings.toLocaleString()}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-neutral-900">
                    {formatCurrency(Number(t.totalRevenue ?? 0), 'CAD', i18n.language)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-neutral-900">
                    {formatCurrency(Number(t.platformFeeCollected ?? 0), 'CAD', i18n.language)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <StripeBadge status={t.stripeConnectStatus} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <FeeCell percent={t.platformFeePercent} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-neutral-500">
                    <RelativeDate iso={t.createdAt} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-neutral-500">
                    <RelativeDate iso={t.lastActivityAt} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pagination && pagination.totalElements > 0 && (
          <div className="flex items-center justify-between border-t border-neutral-100 px-4 py-3 text-xs text-neutral-500">
            <p>
              Showing {pagination.page * pagination.size + 1}–
              {Math.min((pagination.page + 1) * pagination.size, pagination.totalElements)} of{' '}
              {pagination.totalElements}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={pagination.page === 0}
                className="rounded-md border border-neutral-200 px-3 py-1.5 font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={pagination.page + 1 >= pagination.totalPages}
                className="rounded-md border border-neutral-200 px-3 py-1.5 font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    ACTIVE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
    SUSPENDED: 'bg-red-50 text-red-700 border-red-200',
  };
  const cls = styles[status] ?? 'bg-neutral-100 text-neutral-700 border-neutral-200';
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}>
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}

function StripeBadge({ status }: { status: string }) {
  const styles: Record<string, { label: string; cls: string }> = {
    CONNECTED: { label: 'Connected', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    PENDING: { label: 'Onboarding', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    NOT_STARTED: { label: 'Not started', cls: 'bg-neutral-100 text-neutral-600 border-neutral-200' },
  };
  const entry = styles[status] ?? { label: status, cls: 'bg-neutral-100 text-neutral-600 border-neutral-200' };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${entry.cls}`}>
      {entry.label}
    </span>
  );
}

function FeeCell({ percent }: { percent: number | null }) {
  if (percent == null) return <span className="text-neutral-400">—</span>;
  const n = Number(percent);
  const isDefault = Math.abs(n - 5) < 0.001;
  return (
    <span className={isDefault ? 'text-neutral-500' : 'font-medium text-[#C2713A]'}>
      {n.toFixed(2)}%{isDefault ? ' (default)' : ''}
    </span>
  );
}

function RelativeDate({ iso }: { iso: string | null }) {
  if (!iso) return <span className="text-neutral-400">—</span>;
  const d = new Date(iso);
  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffDay = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  let label: string;
  if (diffDay <= 0) label = 'Today';
  else if (diffDay === 1) label = 'Yesterday';
  else if (diffDay < 30) label = `${diffDay} days ago`;
  else if (diffDay < 365) label = `${Math.floor(diffDay / 30)} mo ago`;
  else label = `${Math.floor(diffDay / 365)} yr ago`;

  const absolute = d.toISOString().slice(0, 10);
  return (
    <span title={absolute}>
      {label}
    </span>
  );
}
